import { useEffect, useState } from 'react';
import { useRewardsState } from '../../../coordinators/rewards/hooks/useRewardsState';
import { useHostModeEntryFlow } from '../../../coordinators/rewards/hooks/useHostModeEntryFlow';
import { serviceContainer } from '../../../services/ServiceContainer';
import { DiscordSupportLink, FrozenCallout } from './RewardsErrorStates';
import { PointsBadge, RewardIcon } from './RewardIcon';
import { useRestartCountdown } from './hooks/useRestartCountdown';
import { useRestartStatus } from './hooks/useRestartStatus';
import { useRestartOverlay } from './hooks/useRestartOverlay';
import { RestartPrompt } from './RestartPrompt';
import { RestartConfirm } from './RestartConfirm';
import { ScheduleRestart } from './ScheduleRestart';
import { HostModeOverlay } from './hostMode/HostModeOverlay';
import { useShellToast } from './Toast';
import { useRebootIncentive } from './useRebootIncentive';
import { SettingsGearIcon } from './icons/SettingsGearIcon';
import type { CatalogueSku } from '../../../services/rewards/types';
import { isTierZero } from './tier0/tierZero';
import {
  TIER_0_UNLOCK_ACCESS_CTA,
  TIER_0_HUB_ISSUE_SUBTEXT,
  TIER_0_WELCOME_HEADLINE,
} from './tier0/tier0WelcomeHubCopy';
import { Tier0UnlockAccessLockIcon } from './tier0/Tier0UnlockAccessLockIcon';
interface RewardsHubProps {
  onNavigate: (screen: 'catalogue' | 'settings') => void;
  onNavigateRedeem?: (skuId: string) => void;
  onNavigateUnlockAccess?: () => void;
  driversHealthy: boolean;
  machineTierId?: string | null;
  onScheduleVisibleChange?: (visible: boolean) => void;
  onClose?: () => void;
}

// TODO: Replace with official brand-lockup asset when design provides it.
const PlaycastLogo = () => (
  <a
    href='https://playcast.io'
    target='_blank'
    rel='noopener noreferrer'
    className='inline-flex shrink-0 items-center no-underline'
  >
    <img src='/playcast-logo-horizontal.svg' alt='Playcast' style={{ height: 18 }} className='shrink-0' draggable={false} />
  </a>
);

function GiftCardArt({ imageUrl, name, size = 'lg', pulse }: { imageUrl?: string; name?: string; size?: 'sm' | 'md' | 'lg' | 'hero'; pulse?: boolean }) {
  const maxW = size === 'sm' ? 120 : size === 'md' ? 160 : size === 'hero' ? undefined : 240;
  const aspect = size === 'hero' ? 'aspect-[4/3]' : 'aspect-square';
  const iconSize = size === 'sm' ? 36 : size === 'md' ? 44 : size === 'hero' ? 72 : 64;

  if (pulse) {
    return (
      <div className='relative mx-auto w-full shrink-0' style={{ maxWidth: maxW }}>
        <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
        {imageUrl ? (
          <img src={imageUrl} alt={name ?? ''} className='absolute inset-0 m-auto h-[75%] w-[75%] object-contain' />
        ) : (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-1'>
            <svg width={iconSize} height={iconSize} viewBox='0 0 36 36' aria-hidden='true'>
              <rect x='6' y='12' width='24' height='16' rx='2' fill='none' stroke='rgba(255,255,255,0.4)' strokeWidth='1.4' />
              <path d='M6 14 L18 24 L30 14' fill='none' stroke='rgba(255,255,255,0.4)' strokeWidth='1.4' />
            </svg>
            {name && <p className='text-center text-xs font-semibold text-white'>{name}</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative mx-auto w-full ${aspect} shrink-0 overflow-hidden rounded-[10px] border-2 border-[#FFFFFF]/15 bg-[#FFFFFF]/8 flex flex-col items-center justify-center gap-1.5`}
      style={{ maxWidth: maxW }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name ?? ''} className='absolute inset-0 h-full w-full object-contain' />
      ) : (
        <>
          <svg width={iconSize} height={iconSize} viewBox='0 0 36 36' aria-hidden='true'>
            <rect x='6' y='12' width='24' height='16' rx='2' fill='none' stroke='rgba(255,255,255,0.4)' strokeWidth='1.4' />
            <path d='M6 14 L18 24 L30 14' fill='none' stroke='rgba(255,255,255,0.4)' strokeWidth='1.4' />
          </svg>
          {name && <p className='text-center text-xs font-semibold text-white'>{name}</p>}
        </>
      )}
    </div>
  );
}

const ProgressBar = ({ current, target, isFull }: { current: number; target: number; isFull: boolean }) => {
  const pct = isFull ? 100 : target > 0 ? Math.min((current / target) * 100, 100) : 0;
  return (
    <div className='w-full'>
      <div className='flex items-center justify-center gap-[5px]'>
        <RewardIcon size={22} />
        <span className='text-[22px] font-semibold text-white'>{current.toLocaleString()}</span>
        <span className='text-[22px] text-[#FFFFFF]/70'> / </span>
        <span className='text-[22px] font-semibold text-[#FFFFFF]/80'>{target.toLocaleString()}</span>
      </div>
      <div className='mt-1.5 h-2 w-full overflow-hidden rounded-full'
        style={{ background: 'rgba(255,255,255,0.18)' }}>
        <div
          className='h-full rounded-full transition-all duration-500'
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #F59E0B, #FBBF24, #D946EF, #9333EA)' }}
        />
      </div>
    </div>
  );
};

function getHeroItem(items: CatalogueSku[]): CatalogueSku | null {
  return items.find((item) => !item.comingSoon) ?? null;
}

function RotatingMessage({ messages, index, visible, className = '' }: { messages: string[]; index: number; visible: boolean; className?: string }) {
  if (messages.length === 0) return null;
  return (
    <p
      className={`${className} text-center text-[10px] text-[#FFFFFF]/70 transition-opacity duration-300`}
      style={{ opacity: visible ? 1 : 0 }}
    >
      {messages[index]}
    </p>
  );
}

const AffordabilityModal = ({
  item,
  balance,
  onRedeem,
  onClose,
}: {
  item: CatalogueSku;
  balance: number;
  onRedeem: () => void;
  onClose: () => void;
}) => (
  <>
    <div className='absolute inset-0 z-40 rounded-[12px] bg-[#0b0618] opacity-[0.72]' />
    <div className='absolute inset-0 z-50 flex items-center justify-center p-4'>
      <div className='w-full rounded-[10px] border border-[#FFFFFF]/15 bg-[#0b0618] p-4'>
        <div className='relative mx-auto w-full shrink-0' style={{ maxWidth: 140 }}>
          <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
          {item.imageUrl && (
            <img src={item.imageUrl} alt={item.name} className='absolute inset-0 m-auto h-[75%] w-[75%] object-contain' />
          )}
        </div>
        <p className='mt-3 mb-1.5 text-center text-base font-semibold text-white'>You can afford a gift card</p>
        <p className='mb-[14px] text-center text-xs text-[#FFFFFF]/80'>
          You have {balance.toLocaleString()} tokens — enough to redeem the {item.name}.
          Redeem now or keep saving for greater rewards.
        </p>
        <div className='flex flex-col gap-2'>
          <button type='button' onClick={onRedeem}
            className='flex w-full min-h-[44px] items-center justify-center rounded-full bg-[#FBBF24] text-[15px] font-semibold text-[#020617] transition-transform hover:scale-[1.02] active:scale-[0.98]'>
            Redeem gift card
          </button>
          <button type='button' onClick={onClose}
            className='flex w-full min-h-[44px] items-center justify-center rounded-full text-sm font-medium text-[#FFFFFF]/80 transition-opacity hover:opacity-80'>
            Close
          </button>
        </div>
      </div>
    </div>
  </>
);

export const RewardsHub = ({
  onNavigate,
  onNavigateRedeem,
  onNavigateUnlockAccess,
  driversHealthy,
  machineTierId,
  onScheduleVisibleChange,
  onClose,
}: RewardsHubProps) => {
  const {
    profile,
    profileLoading,
    profileError,
    catalogue,
    catalogueLoading,
    catalogueError,
    hostMessages,
    hostMessageIndex,
    hostMessageVisible,
    refreshProfile,
    refreshCatalogue,
    affordableItem,
    showAffordabilityModal,
    dismissAffordability,
    inboxUnreadCount,
  } = useRewardsState();
  const hasUnread = inboxUnreadCount > 0;
  const unreadCount = inboxUnreadCount;

  const isPendingRestart = !driversHealthy;
  const isTier0Machine = isTierZero(machineTierId);
  /** Same hub chrome as pending-restart (machine not ready to earn). Tier 0 uses this layout for shell editing. */
  const showMachineIssueHub = isPendingRestart || isTier0Machine;
  const showTier0HubMessaging = isTier0Machine && !isPendingRestart;
  const tier0Headline = TIER_0_WELCOME_HEADLINE;
  const tier0HubSubtext = TIER_0_HUB_ISSUE_SUBTEXT;
  const tier0UnlockCta = TIER_0_UNLOCK_ACCESS_CTA;
  const tier0HubFooter = 'Your machine could be earning rewards';
  const restartStatus = useRestartStatus();
  const countdown = useRestartCountdown(restartStatus.scheduled ? restartStatus.scheduledTimeUnix : null);
  const hostModeFlow = useHostModeEntryFlow();
  const showToast = useShellToast();
  const rebootLabel = useRebootIncentive();

  useEffect(() => {
    if (hostModeFlow.error) showToast(hostModeFlow.error);
  }, [hostModeFlow.error, showToast]);

  useEffect(() => {
    if (restartStatus.lastError) showToast(restartStatus.lastError);
  }, [restartStatus.lastError, showToast]);

  const hasIncentive = rebootLabel.includes('tokens');
  const { overlay: restartOverlay, dispatch: dispatchOverlay } = useRestartOverlay(driversHealthy, restartStatus.scheduled, hasIncentive);

  // Notify parent when schedule page is active (so it can hide the close button)
  useEffect(() => {
    onScheduleVisibleChange?.(restartOverlay === 'schedule');
  }, [restartOverlay, onScheduleVisibleChange]);

  useEffect(() => {
    const variant = isTierZero(machineTierId)
      ? 'tier-0'
      : isPendingRestart
        ? 'pending-restart'
        : 'normal';
    serviceContainer.analyticsManager.sendRewardsHubVariantShownEvent({
      variant,
      hasScheduledReboot: restartStatus.scheduled,
    });
  }, [isPendingRestart, restartStatus.scheduled, machineTierId]);

  const heroItem = getHeroItem(catalogue);
  const canAffordHero = heroItem != null && (profile?.pointsBalance ?? 0) >= heroItem.pointCost;

  const [heroImageReady, setHeroImageReady] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setHeroImageReady(true);
    img.onerror = () => setHeroImageReady(true);
    img.src = '/playcast-hero-gradient.svg';
    if (img.complete) setHeroImageReady(true);
  }, []);

  const isFullyLoaded = !!(profile && !profileLoading && !catalogueLoading && heroImageReady);

  // Error states must precede the loading gate: a failed fetch never satisfies
  // the gate, so loading-first would hide failures behind a permanent spinner.
  if (profileError && !profile) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 px-4'>
        <p className='text-sm text-[#FCA5A5]'>Failed to load rewards profile</p>
        <button type='button' onClick={refreshProfile}
          className='rounded-lg border border-[#FFFFFF]/15 bg-[#FFFFFF]/8 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80'>
          Retry
        </button>
        <DiscordSupportLink />
      </div>
    );
  }

  if (catalogueError && catalogue.length === 0) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 px-4'>
        <p className='text-sm text-[#FCA5A5]'>Failed to load rewards catalog</p>
        <button type='button' onClick={refreshCatalogue}
          className='rounded-lg border border-[#FFFFFF]/15 bg-[#FFFFFF]/8 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80'>
          Retry
        </button>
        <DiscordSupportLink />
      </div>
    );
  }

  if (!isFullyLoaded) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center'>
        <img src='/playcast-gibber.svg' alt='Playcast' className='w-[139px] h-[112px] shrink-0 object-contain' />
        <h1 className='shrink-0 text-[32px] font-bold tracking-tight text-[#FBBF24]' style={{ fontFamily: 'Inter' }}>Playcast</h1>
        <div className='mt-8 shrink-0 rounded-full bg-[#FBBF24]/60 px-12 py-2.5 flex items-center justify-center cursor-default'>
          <div className='h-6 w-6 animate-spin rounded-full border-[3px] border-[#020617]/20 border-t-[#020617]' />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { pointsBalance, frozen } = profile;

  if (restartOverlay === 'schedule') {
    return (
      <ScheduleRestart
        onSchedule={(epochSec) => {
          console.debug('[RewardsHub] onSchedule fired — epochSec=%d', epochSec);
          restartStatus.scheduleReboot(epochSec);
          dispatchOverlay({ type: 'DISMISS' });
        }}
        onRestartNow={() => dispatchOverlay({ type: 'CONFIRM' })}
        lastError={restartStatus.lastError}
      />
    );
  }

  return (
    <>
      <div
        className='relative flex min-h-0 flex-1 flex-col'
        style={{ opacity: showAffordabilityModal || restartOverlay === 'almost-ready' || restartOverlay === 'confirm-restart' ? 0.35 : 1 }}
      >
        {/* Top bar: logo left, balance pill right */}
        <div className='mb-2 flex h-[22px] w-full shrink-0 items-center gap-2'>
          <div className='flex shrink-0 items-center'><PlaycastLogo /></div>
          <div className='min-w-2 flex-1' aria-hidden='true' />
          <PointsBadge balance={pointsBalance} />
          {onClose ? (
            <button
              type='button'
              onClick={onClose}
              className='flex h-[22px] w-[22px] shrink-0 items-center justify-center border-none bg-transparent p-0 transition-opacity hover:opacity-80'
              aria-label='Close'
            >
              <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
                <path
                  d='M6.4 6.4 L17.6 17.6 M17.6 6.4 L6.4 17.6'
                  fill='none'
                  stroke='rgba(255,255,255,0.8)'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </button>
          ) : null}
        </div>

        {/* === TOP GROUP === */}
        <div className='shrink-0 flex flex-col items-center'>
          {showMachineIssueHub ? (
            <>
              {heroItem ? (
                <GiftCardArt imageUrl={heroItem.imageUrl} name={heroItem.name} size='lg' pulse />
              ) : (
                <div className='relative mx-auto w-full shrink-0' style={{ maxWidth: 240 }}>
                  <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
                  <img src='/playcast-token.svg' alt='Playcast Token' className='absolute inset-0 m-auto h-[40%] w-[40%] object-contain' />
                </div>
              )}

              <div className='mt-3 w-full'>
                {showTier0HubMessaging ? (
                  <div className='flex flex-col items-center gap-2' style={{ maxWidth: 280, margin: '0 auto' }}>
                    <p
                      className='m-0 text-center font-bold'
                      style={{ color: '#FBBF24', fontSize: 20, lineHeight: 1.15 }}
                    >
                      {tier0Headline}
                    </p>
                    <p
                      className='m-0 text-center font-medium text-[#FFFFFF]/55'
                      style={{ fontSize: 12, lineHeight: '15px', maxWidth: 280 }}
                    >
                      {tier0HubSubtext}
                    </p>
                  </div>
                ) : (
                  <div className='flex flex-col items-center gap-2'>
                    <p className='text-center text-base font-semibold text-[#FBBF24]'>Pending Restart</p>
                    <p className='text-center text-xs text-[#FFFFFF]/55' style={{ maxWidth: 260 }}>
                      You won&apos;t start earning tokens until you restart to finish setup.
                    </p>
                    {countdown ? (
                      <p className='text-center text-xs font-semibold text-[#FBBF24]'>
                        Auto-restart in {countdown}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            </>
          ) : heroItem ? (
            <>
              <GiftCardArt imageUrl={heroItem.imageUrl} name={heroItem.name} size='lg' pulse />

              <div className='mt-3 w-full'>
                {frozen ? (
                  <FrozenCallout />
                ) : (
                  <>
                    <ProgressBar current={pointsBalance} target={heroItem.pointCost} isFull={canAffordHero} />

                    {canAffordHero ? (
                      <>
                        <div className='mt-2 flex justify-center'>
                          <button type='button' onClick={() => onNavigateRedeem?.(heroItem.skuId)}
                            style={{ minHeight: 28, padding: '5px 18px', borderRadius: 9999, fontSize: 12 }}
                            className='border-none bg-[#FFFFFF]/20 font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]'>
                            Redeem
                          </button>
                        </div>
                        <p className='mt-1.5 text-center text-xs font-semibold text-white'>
                          You can redeem {Math.floor(pointsBalance / heroItem.pointCost)} gift card{Math.floor(pointsBalance / heroItem.pointCost) > 1 ? 's' : ''} right now!
                        </p>
                      </>
                    ) : (
                      <p className='mt-2 mb-6 text-center text-xs text-[#FFFFFF]/80'>
                        {(heroItem.pointCost - pointsBalance).toLocaleString()} tokens until you can afford your next card
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div className='relative mx-auto w-full shrink-0' style={{ maxWidth: 240 }}>
                <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
                <img src='/playcast-token.svg' alt='Playcast Token' className='absolute inset-0 m-auto h-[40%] w-[40%] object-contain' />
              </div>
              <div className='mt-3 flex items-center justify-center gap-[5px]'>
                <RewardIcon size={22} />
                <span className='text-[22px] font-semibold text-white'>{pointsBalance.toLocaleString()}</span>
              </div>
              <p className='mt-1 mb-6 text-center text-xs text-[#FFFFFF]/80'>
                New rewards coming soon — keep earning!
              </p>
            </>
          )}
        </div>

        {/* Absorbs leftover height so the bottom chrome stays pinned whether or
            not the hero includes a progress bar / inventory card. */}
        <div className='min-h-0 flex-1' aria-hidden='true' />

        {/* === BOTTOM GROUP === */}
        <div className='shrink-0 flex flex-col items-center'>
          {!showMachineIssueHub && (
            <RotatingMessage messages={hostMessages} index={hostMessageIndex} visible={hostMessageVisible} />
          )}

          {showMachineIssueHub && (
            showTier0HubMessaging ? (
              <button
                type='button'
                onClick={() => onNavigateUnlockAccess?.()}
                style={{ minHeight: 44, padding: '12px 24px', borderRadius: 9999, fontSize: 15 }}
                className='mb-2 flex w-full items-center justify-center gap-1.5 border-none bg-[#FBBF24] font-semibold text-[#141414] transition-transform hover:scale-[1.02] active:scale-[0.98]'
              >
                <Tier0UnlockAccessLockIcon color="#141414" />
                {tier0UnlockCta}
              </button>
            ) : (
              <button type='button' onClick={() => dispatchOverlay({ type: 'CONFIRM' })}
                style={{ minHeight: 44, padding: '12px 24px', borderRadius: 9999, fontSize: 15 }}
                className='mb-2 w-full border-none bg-[#FBBF24] font-semibold text-[#020617] transition-transform hover:scale-[1.02] active:scale-[0.98]'>
                {rebootLabel}
              </button>
            )
          )}

          {!showMachineIssueHub && (
            <button
              type='button'
              disabled={hostModeFlow.inFlight}
              onClick={() => hostModeFlow.start()}
              style={{ minHeight: 44, padding: '12px 24px', borderRadius: 9999, fontSize: 15 }}
              className='mb-2 w-full border-none bg-[#FBBF24] font-semibold text-[#141414] transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'>
              Activate Host Mode
            </button>
          )}

          <button type='button' onClick={() => onNavigate('catalogue')}
            style={{
              minHeight: 44, padding: '12px 24px', borderRadius: 9999, fontSize: 15,
              border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.067)',
            }}
            className='w-full font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]'>
            Catalog
          </button>
          <div className='mt-2 flex w-full shrink-0 items-center justify-end pr-[2px]'>
            <button type='button' onClick={() => onNavigate('settings')}
              aria-label={`Settings${unreadCount > 0 ? ` — ${unreadCount} unread notifications` : ''}`}
              className='border-none bg-transparent p-0 transition-opacity hover:opacity-80'>
              <SettingsGearIcon width={20} badge={hasUnread} />
            </button>
          </div>
          <p className='mt-1.5 text-center text-[10px] text-[#FFFFFF]/60'>
            {showTier0HubMessaging
              ? tier0HubFooter
              : 'Your machine earns rewards while idle in Host Mode'}
          </p>
        </div>
      </div>

      {/* Restart overlay modals */}
      {restartOverlay === 'almost-ready' && (
        <RestartPrompt
          onRestartNow={() => dispatchOverlay({ type: 'CONFIRM' })}
          onRestartLater={() => dispatchOverlay({ type: 'SCHEDULE' })}
        />
      )}

      {restartOverlay === 'confirm-restart' && (
        <RestartConfirm
          onRestartNow={() => restartStatus.restartNow('confirm')}
          onNotNow={() => dispatchOverlay({ type: 'SCHEDULE' })}
        />
      )}

      {showAffordabilityModal && affordableItem && (
        <AffordabilityModal
          item={affordableItem}
          balance={profile?.pointsBalance ?? 0}
          onRedeem={() => { dismissAffordability(); onNavigateRedeem?.(affordableItem.skuId); }}
          onClose={() => { dismissAffordability(); }}
        />
      )}

      {hostModeFlow.phase !== 'idle' && (
        <HostModeOverlay
          phase={hostModeFlow.phase}
          categorized={hostModeFlow.categorized}
          failureCode={hostModeFlow.failureCode}
          onDismiss={hostModeFlow.dismiss}
          onConfirmRestart={hostModeFlow.confirmRestart}
          restartNow={() => restartStatus.restartNow('confirm')}
        />
      )}
    </>
  );
};
